import React, { forwardRef, useCallback, useEffect, useState } from "react";
import DOMPurify from "dompurify";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
const API_ORIGIN = API_BASE_URL.replace(/\/api$/i, "");

// Slugs of the CMS pages admins edit in the admin dashboard (seeded by admin-api seeder/cms.js)
export const CMS_SLUGS = {
  TERMS: "terms-and-conditions",
  PRIVACY_POLICY: "privacy-policy",
};

// One request per slug per tab, shared by every page that shows it; a failed load is retried next time
const pageRequests = new Map();

export const fetchCmsPage = (slug) => {
  if (!pageRequests.has(slug)) {
    const request = fetch(`${API_ORIGIN}/admin-api/cms/content`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ slug }),
    }).then(async (response) => {
      const data = await response.json();
      const page = data?.data?.page;
      if (!response.ok || !page?.content) throw new Error(data?.message || "Page not available");
      return page;
    });
    request.catch(() => pageRequests.delete(slug));
    pageRequests.set(slug, request);
  }
  return pageRequests.get(slug);
};

// status: "idle" (not requested yet) | "loading" | "ready" | "error"
export const useCmsPage = (slug, enabled = true) => {
  const [state, setState] = useState({ status: "idle", page: null });

  const load = useCallback(() => {
    setState({ status: "loading", page: null });
    fetchCmsPage(slug)
      .then((page) => setState({ status: "ready", page }))
      .catch(() => setState({ status: "error", page: null }));
  }, [slug]);

  useEffect(() => {
    if (enabled) load();
  }, [enabled, load]);

  return { ...state, reload: load };
};

// CMS pages are HTML; Tailwind has no typography plugin here, so their tags are styled directly
const CONTENT_STYLES =
  "text-[15px] leading-7 text-[#3F4A45] [&_h1]:text-xl [&_h1]:font-semibold [&_h1]:text-[#161F28] [&_h1]:mt-6 [&_h1]:mb-2 " +
  "[&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-[#161F28] [&_h2]:mt-6 [&_h2]:mb-2 [&_h3]:font-semibold [&_h3]:text-[#095228] [&_h3]:mt-4 [&_h3]:mb-1 " +
  "[&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-3 [&_li]:mb-1 " +
  "[&_a]:text-[#006838] [&_a]:underline [&_strong]:text-[#161F28] [&>*:first-child]:mt-0";

// Admin-written HTML is sanitized before it reaches the page
export const CmsHtml = forwardRef(({ html, className = "", ...props }, ref) => (
  <div
    ref={ref}
    {...props}
    className={`${CONTENT_STYLES} ${className}`}
    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html || "") }}
  />
));
